import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import {
    Bold as BoldIcon,
    Italic as ItalicIcon,
    Link2,
    List as ListIcon,
    Image as ImageIcon,
    Undo,
    Redo,
} from "lucide-react";

// Bold/Italic with `inclusive: false` so the cursor sitting at the end of a
// bold/italic word is treated as OUTSIDE the mark — typing there produces
// normal text instead of silently continuing bold/italic. This was the
// underlying cause of the "retyping a word makes everything bold" bug the
// agent kept hitting when Claude occasionally emphasised names/comps.
const NonInclusiveBold = Bold.extend({ inclusive: false });
const NonInclusiveItalic = Italic.extend({ inclusive: false });

/**
 * Rich text editor for cover letters and per-editor emails.
 *
 * - Value is HTML (`<p>`, `<em>`, `<strong>`, `<a>`, `<ul>`, `<li>`, `<br>`).
 * - Backwards compatible with plain-text values (wraps in a `<p>` on load).
 * - Emits HTML on every change via `onChange`. Empty content → empty string.
 */
export function RichEditor({
    value,
    onChange,
    placeholder,
    minHeight = 220,
    className = "",
    editorClassName = "",
    dataTestId,
    enableImages = false,          // opt-in: shows an "Upload image" button
    maxImageSizeKb = 400,           // per-image cap when embedding as base64
}) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,        // agents want prose, not headings
                blockquote: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
                bold: false,           // replaced by NonInclusiveBold below
                italic: false,         // replaced by NonInclusiveItalic below
                link: false,           // custom Link configured below
            }),
            NonInclusiveBold,
            NonInclusiveItalic,
            Link.configure({
                openOnClick: false,    // editing, not navigating
                autolink: true,
                linkOnPaste: true,
                HTMLAttributes: {
                    rel: "noopener noreferrer",
                    target: "_blank",
                    class: "underline text-[#8C2D19]",
                },
            }),
            Placeholder.configure({ placeholder: placeholder || "" }),
            // Always load the Image extension so <img> tags in the incoming
            // value (typically base64-embedded signature graphics) render
            // in the editor and, more importantly, don't get silently stripped
            // when the editor round-trips the HTML. The toolbar "insert
            // image" button is still gated by `enableImages`.
            Image.configure({
                inline: false,
                allowBase64: true,
                HTMLAttributes: { class: "max-w-full h-auto rounded-sm" },
            }),
        ],
        content: htmlFromValue(value),
        onUpdate: ({ editor }) => {
            const html = editor.isEmpty ? "" : editor.getHTML();
            onChange(html);
        },
        editorProps: {
            attributes: {
                class:
                    "prose prose-neutral max-w-none focus:outline-none " +
                    "font-serif text-base leading-relaxed [&_a]:underline [&_a]:text-[#8C2D19] " +
                    editorClassName,
                "data-testid": dataTestId ? `${dataTestId}-editable` : undefined,
            },
        },
    });

    // Sync external value changes (e.g. AI generation replaces the body)
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const incoming = htmlFromValue(value);
        // Avoid an infinite loop: only reset when the incoming value is
        // meaningfully different from what the editor currently shows.
        if (normalize(current) !== normalize(incoming)) {
            editor.commands.setContent(incoming, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) return null;

    const promptForLink = () => {
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("Enter URL (or leave empty to remove)", prev);
        if (url === null) return; // cancelled
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        // Auto-prefix with https:// if the user typed a bare domain
        const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        editor.chain().focus().extendMarkRange("link").setLink({ href: normalized }).run();
    };

    const promptForImage = () => {
        // Trigger a hidden file picker
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const kb = Math.round(file.size / 1024);
            if (kb > maxImageSizeKb) {
                alert(`Image is ${kb} KB. Please pick something under ${maxImageSizeKb} KB — smaller images keep the email lightweight and reliable in mailboxes.`);
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = String(reader.result || "");
                if (!dataUrl.startsWith("data:image/")) return;
                editor.chain().focus().setImage({ src: dataUrl }).run();
            };
            reader.readAsDataURL(file);
        };
        input.click();
    };

    return (
        <div className={"border border-border rounded-sm bg-white/60 " + className} data-testid={dataTestId}>
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-secondary/40" data-testid={dataTestId ? `${dataTestId}-toolbar` : undefined}>
                <ToolbarBtn
                    active={editor.isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    label="Bold (⌘B)"
                    testId={dataTestId ? `${dataTestId}-bold` : undefined}
                >
                    <BoldIcon className="w-3.5 h-3.5" strokeWidth={2} />
                </ToolbarBtn>
                <ToolbarBtn
                    active={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    label="Italic (⌘I)"
                    testId={dataTestId ? `${dataTestId}-italic` : undefined}
                >
                    <ItalicIcon className="w-3.5 h-3.5" strokeWidth={2} />
                </ToolbarBtn>
                <ToolbarBtn
                    active={editor.isActive("link")}
                    onClick={promptForLink}
                    label="Add / edit link (⌘K)"
                    testId={dataTestId ? `${dataTestId}-link` : undefined}
                >
                    <Link2 className="w-3.5 h-3.5" strokeWidth={2} />
                </ToolbarBtn>
                <div className="w-px h-4 bg-border mx-1" />
                <ToolbarBtn
                    active={editor.isActive("bulletList")}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    label="Bullet list"
                    testId={dataTestId ? `${dataTestId}-bullet` : undefined}
                >
                    <ListIcon className="w-3.5 h-3.5" strokeWidth={2} />
                </ToolbarBtn>
                {enableImages && (
                    <ToolbarBtn
                        onClick={promptForImage}
                        label={`Insert image (max ${maxImageSizeKb} KB)`}
                        testId={dataTestId ? `${dataTestId}-image` : undefined}
                    >
                        <ImageIcon className="w-3.5 h-3.5" strokeWidth={2} />
                    </ToolbarBtn>
                )}
                <div className="w-px h-4 bg-border mx-1" />
                <ToolbarBtn
                    onClick={() => editor.chain().focus().undo().run()}
                    label="Undo (⌘Z)"
                    testId={dataTestId ? `${dataTestId}-undo` : undefined}
                >
                    <Undo className="w-3.5 h-3.5" strokeWidth={2} />
                </ToolbarBtn>
                <ToolbarBtn
                    onClick={() => editor.chain().focus().redo().run()}
                    label="Redo (⌘⇧Z)"
                    testId={dataTestId ? `${dataTestId}-redo` : undefined}
                >
                    <Redo className="w-3.5 h-3.5" strokeWidth={2} />
                </ToolbarBtn>
            </div>
            <EditorContent
                editor={editor}
                className="px-4 py-3"
                style={{ minHeight }}
            />
        </div>
    );
}

function ToolbarBtn({ active, onClick, label, testId, children }) {
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            onClick={onClick}
            data-testid={testId}
            className={
                "p-1.5 rounded-sm transition-colors " +
                (active
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-background/60")
            }
        >
            {children}
        </button>
    );
}

/** Detect whether a value looks like HTML; if not, wrap it in a paragraph. */
function htmlFromValue(value) {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    if (/<\/?(p|br|em|strong|a|ul|ol|li|div|span|img)\b/i.test(trimmed)) return trimmed;
    // Plain text: preserve paragraph breaks (blank line = new <p>).
    return trimmed
        .split(/\n{2,}/)
        .map((para) => `<p>${escapeHtml(para).replace(/\n/g, "<br>")}</p>`)
        .join("");
}

function escapeHtml(s) {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function normalize(html) {
    return String(html || "").replace(/\s+/g, " ").trim();
}

/** Strip HTML down to a plain-text fallback (used by the backend text/plain email part). */
export function htmlToPlainText(html) {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(String(html), "text/html");
    // Convert <br> and block boundaries to newlines
    doc.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
    doc.querySelectorAll("p, li, div").forEach((el) => {
        el.appendChild(doc.createTextNode("\n"));
    });
    return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}
